import React, { useState } from 'react';
import { Container, Box } from '@mui/material';
import styles from './LoginPage.module.scss';
import { useNavigate } from 'react-router-dom';
import LoginSuccessAnimation from '../LoginSuccessAnimation';
import { DashboardRoute } from '../../router/routes';
import { setLoginStorageData } from '../../api/loginApiHelper';
import { LoginPageTitle } from './LoginPageTitle';
import { LoginPageDescription } from './LoginPageDescription';
import { LoginPageButton } from './LoginPageButton';
import { LoginPageVerificationCode } from './LoginPageVerificationCode';
import { LoginPageMain } from './LoginPageMain';
import { useSessionAction } from '../../providers/sessionActionProvider';
import { ENV } from '../../config/ENV';

export interface LoginPageProps {}

const loginBackgroundImage = ENV.LOGIN_BACKGROUND_URL;

const LoginPage: React.FC<LoginPageProps> = () => {
  const [email, setEmail] = useState<string>('');
  const [emailVerifyError, setEmailVerifyError] = useState<boolean>(false);
  const [isCodeSent, setIsCodeSent] = useState<boolean>(false);
  const [loginSuccess, setLoginSuccess] = useState<boolean>(false);
  const navigate = useNavigate();
  const { logInUser } = useSessionAction();

  const renderLoginContent = () => {
    if (emailVerifyError) {
      return (
        <Box sx={{ width: '100%', marginBottom: 2 }}>
          <LoginPageTitle text="Oops! Email not found" />
          <LoginPageDescription
            text="Seems like we can't find your email. Try entering your email again
            or contact your administrator for help."
          />
          <LoginPageButton
            onClick={() => {
              setEmail('');
              setEmailVerifyError(false);
            }}
          >
            Try Again
          </LoginPageButton>
        </Box>
      );
    } else if (loginSuccess) {
      return <LoginSuccessAnimation />;
    } else if (isCodeSent) {
      return (
        <LoginPageVerificationCode
          email={email}
          onVerified={async (verifyResponse) => {
            setLoginSuccess(true);
            const managerId = verifyResponse.managerId;
            const trainerId = verifyResponse.trainerId;
            const memberId = verifyResponse.memberId;
            const loginExpiration = verifyResponse.loginExpiration;

            const loginData = {
              managerId,
              trainerId,
              memberId,
              loginExpiration,
            };
            setLoginStorageData(loginData);
            const success = await logInUser(managerId, trainerId, memberId);
            if (success) {
              navigate(DashboardRoute.Dashboard);
            } else {
              setEmailVerifyError(true);
            }
          }}
        />
      );
    }
    return (
      <LoginPageMain
        email={email}
        setEmail={setEmail}
        setEmailVerifyError={setEmailVerifyError}
        setCodeSent={() => setIsCodeSent(true)}
      />
    );
  };

  return (
    <Container
      component="main"
      className={styles.container}
      style={{ padding: 0, display: 'flex', margin: 0, maxWidth: '100vw' }}
    >
      <Box
        className={styles.leftSide}
        sx={{
          backgroundImage: `url(${loginBackgroundImage})`,
        }}
      />
      <Box className={styles.rightSide}>
        <Box className={styles.rightSideContentWrapper}>
          {renderLoginContent()}
        </Box>
      </Box>
    </Container>
  );
};

export default LoginPage;
